import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import refreshIcon from "../assets/images/refresh.svg";
import flagIcon from "../assets/images/redFlag.png";

const TherapistClients = ({
  onViewHistory,
  clientsData,
  fetchClientsData,
  loading: externalLoading,
  isAdmin
}) => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unlocking, setUnlocking] = React.useState(new Set());
  const preferredSpaceCache = {};

  const [meetingsMap, setMeetingsMap] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: "name", direction: "asc" });


  
  useEffect(() => {
    console.log("🪵 DEBUG: useEffect triggered with clientsData =", clientsData);
    if (clientsData) {
      console.log("🪵 DEBUG: Processing clientsData prop...");
      processClientsData(clientsData);
    } else if (fetchClientsData && sessions.length === 0 && !externalLoading) {
      console.log("🪵 DEBUG: Calling fetchClientsData()...");
      fetchClientsData().then((data) => {
        console.log("🪵 DEBUG: fetchClientsData returned =", data);
        if (data) {
          processClientsData(data);
        }
      });
    } else if (!fetchClientsData && sessions.length === 0) {
      console.log("🪵 DEBUG: Calling fetchSessions()...");
      fetchSessions();
    }
  }, [clientsData]);

  const fetchPreferredSpace = async (clientId) => {
  // If already fetched once → return cached value
  if (preferredSpaceCache[clientId]) {
    console.log("🟢 Returning cached space for", clientId);
    return preferredSpaceCache[clientId];
  }

  try {
    const res = await axiosInstance.get(`/api/get-selected-space/${clientId}`);
    const space = res.data?.selectedSpace || "Not selected";

    // Cache it to avoid duplicate API calls
    preferredSpaceCache[clientId] = space;

    console.log("🌴 Fetched and cached preferred space for", clientId, "→", space);

    return space;
  } catch (err) {
    console.error("❌ Failed preferredSpace fetch for", clientId, err);
    return "Not selected";
  }
};



  const processClientsData = (data) => {
    try {
      console.log("🪵 DEBUG: processClientsData input =", data);
      setError(null);

      if (data && data.clients) {
  console.log(`🪵 DEBUG: Found ${data.clients.length} clients`);
  const allSessions = [];

  const fetchAllPreferredSpaces = data.clients.map(async (client) => {
    const preferredSpace = await fetchPreferredSpace(client.id);

    if (client.sessions && client.sessions.length > 0) {
      client.sessions.forEach((session) => {
        allSessions.push({
          ...session,
          clientName: client.name,
          clientId: client.id,
          clientEmail: client.email,
          clientNickname: client.nickname,
          preferredSpace,     // ⭐ coming from new endpoint
          userType: client.userType || "Client",
        });
      });
    } else {
      allSessions.push({
        sessionID: null,
        sessionName: null,
        timestamp: null,
        summary: null,
        clientName: client.name,
        clientId: client.id,
        clientEmail: client.email,
        clientNickname: client.nickname,
        preferredSpace,     // ⭐ correct value even if no sessions
        userType: client.userType || "Client",
        sessionCount: 0,
        isPlaceholder: true,
      });
    }
  });

  // Wait until all preferred space calls finish
  Promise.all(fetchAllPreferredSpaces).then(() => {
    console.log("🌴 Final allSessions with preferredSpace:", allSessions);
    setSessions(allSessions);
    setLoading(false);
  });

  return; // prevent the old code from running below
}
 else if (data && data.summaries) {
        console.log(`🪵 DEBUG: Data has summaries array with length=${data.summaries.length}`);
        const processedSessions = processSessionSummaries(data.summaries);
        setSessions(processedSessions);
      } else {
        console.log("⚠️ DEBUG: No clients or summaries found in data");
        setSessions([]);
      }
    } catch (err) {
      console.error("❌ DEBUG: Error processing clients data:", err);
      setError("Failed to process session data");
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSessions = async () => {
    try {
      console.log("🪵 DEBUG: Fetching /api/summarize...");
      setLoading(true);
      setError(null);

      const response = await axiosInstance.get("/api/summarize");
      console.log("🪵 DEBUG: API /api/summarize response:", response);

      if (response.data) {
        console.log("🪵 DEBUG: Response keys:", Object.keys(response.data));
        if (response.data.clients) {
          console.log(`🪵 DEBUG: Found ${response.data.clients.length} clients`);
          const allSessions = [];
          response.data.clients.forEach((client, idx) => {
            console.log(`🪵 DEBUG: [Client ${idx}] id=${client.id}, name=${client.name}, sessions=${client.sessions?.length || 0}`);
            if (client.sessions && client.sessions.length > 0) {
              client.sessions.forEach((session, sIdx) => {
                console.log(`   🪵 DEBUG: [Session ${sIdx}] sessionID=${session.sessionID}, timestamp=${session.timestamp}`);
                allSessions.push({
                  ...session,
                  clientName: client.name,
                  clientId: client.id,
                  clientEmail: client.email,
                  clientNickname: client.nickname,
                  preferredSpace: client.preferredSpace,
                  userType: client.userType || "Client",
                });
              });
            } else {
              console.log(`   ⚠️ DEBUG: Client ${client.id} has NO sessions`);
              allSessions.push({
                sessionID: null,
                sessionName: null,
                timestamp: null,
                summary: null,
                clientName: client.name,
                clientId: client.id,
                clientEmail: client.email,
                clientNickname: client.nickname,
                preferredSpace: client.preferredSpace || "No sessions yet",
                userType: client.userType || "Client",
                sessionCount: 0,
                isPlaceholder: true,
              });
            }
          });

          console.log("🪵 DEBUG: Final allSessions array:", allSessions);
          setSessions(allSessions);
        } else if (response.data.summaries) {
          console.log("🪵 DEBUG: Found summaries array, length =", response.data.summaries.length);
          const processedSessions = processSessionSummaries(response.data.summaries);
          setSessions(processedSessions);
        } else {
          console.log("⚠️ DEBUG: No clients or summaries found in API response");
          setSessions([]);
        }
      } else {
        console.log("⚠️ DEBUG: Response.data is empty");
        setSessions([]);
      }
    } catch (err) {
      console.error("❌ DEBUG: Error fetching sessions:", err);
      setError(err.response?.data?.message || "Failed to fetch sessions");
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  const processSessionSummaries = (summaries) => {
    console.log("🪵 DEBUG: processSessionSummaries called with", summaries.length, "items");
    const processedSessions = summaries.map((summary, idx) => {
      const participantMatch = summary.summary.match(/\*\*Participants?:\*\*\s*([^\n]+)/);
      const participant = participantMatch ? participantMatch[1].trim() : "Unknown Client";
      const clientId = participant.toLowerCase().replace(/\s+/g, "-");

      console.log(`🪵 DEBUG: [Summary ${idx}] participant=${participant}, sessionID=${summary.sessionID}`);
      return {
        sessionID: summary.sessionID,
        sessionName: summary.sessionName,
        timestamp: summary.timestamp,
        summary: summary.summary,
        clientName: participant,
        clientId: clientId,
        clientNickname: participant,
        preferredSpace: summary.sessionName || "No preference",
        userType: "Client",
      };
    });

    console.log("🪵 DEBUG: Returning processedSessions:", processedSessions);
    return processedSessions.sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));
  };
  const fetchSessionByName = async (sessionName) => {
  try {
    const response = await axiosInstance.get(
      `/items/by-sessionname/${encodeURIComponent(sessionName)}`
    );
    return response.data;
  } catch (err) {
    console.error("❌ Failed to fetch session by name:", err);
    return null;
  }
};

useEffect(() => {
  let interval;

  const fetchMeetings = async () => {
    try {
      const userId = localStorage.getItem("PlutonicuserID");

      const res = await axiosInstance.get(`/api/GetMeetings/${userId}`);

      const map = {};

      res.data.forEach((room) => {
        map[room.meetingID] = {
          meetingID: room.meetingID,
          userIds: room.PlutonicuserID,
          onlineArray: room.online,
          sessionTime: room.sessionTime,
        };
      });

      setMeetingsMap(map);
    } catch (err) {
      console.error("❌ Failed to fetch meetings", err);
    }
  };

  fetchMeetings();
  interval = setInterval(fetchMeetings, 5000);

  return () => clearInterval(interval);
}, []);

const extractRoomId = (name = "") => {
  const n = (name || "").toLowerCase();
  if (n.includes("beach")) return "beach";
  if (n.includes("forest")) return "mountain";
  if (n.includes("park")) return "park";
  if (n.includes("camp")) return "campsite";
  if (n.includes("winter")) return "winter-holiday";
  return null;
};

const getClientOnlineSession = (session) => {
  if (!session?.clientId) return null;

  for (const meeting of Object.values(meetingsMap)) {
    const index = meeting.userIds?.findIndex(
      (id) => id === session.clientId
    );

    if (index >= 0 && meeting.onlineArray?.[index]) {
      const roomType = meeting.meetingID
        .split(".")[0]
        .replace("-therapy", "");

      return {
        meetingID: meeting.meetingID,
        roomType,
      };
    }
  }

  return null;
};

const getClientNextSession = (session) => {
  if (!session?.clientId) return { status: "none" };

  const now = Date.now();
  const availableBeforeMs = 15 * 60 * 1000;
  const availableAfterMs = 75 * 60 * 1000;
  const uniqueSessions = new Map();

  Object.values(meetingsMap).forEach((meeting) => {
    if (!meeting.userIds?.includes(session.clientId) || !meeting.sessionTime) {
      return;
    }

    const start = new Date(meeting.sessionTime);
    if (Number.isNaN(start.getTime())) return;

    // Each scheduled session has one row per therapy space. Show it once.
    const meetingParts = meeting.meetingID?.split(".") || [];
    const sessionKey = meetingParts[1] || start.toISOString();
    if (!uniqueSessions.has(sessionKey)) uniqueSessions.set(sessionKey, start);
  });

  const scheduledSessions = Array.from(uniqueSessions.values()).sort(
    (a, b) => a - b
  );
  const activeSession = scheduledSessions.find((start) => {
    const timeUntilStart = start.getTime() - now;
    return (
      timeUntilStart <= availableBeforeMs &&
      timeUntilStart >= -availableAfterMs
    );
  });

  if (activeSession) return { status: "active", start: activeSession };

  const upcomingSession = scheduledSessions.find(
    (start) => start.getTime() > now
  );

  return upcomingSession
    ? { status: "upcoming", start: upcomingSession }
    : { status: "none" };
};

const formatSessionTime = (date) =>
  date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

const handleSort = (key) => {
  setSortConfig((current) => ({
    key,
    direction:
      current.key === key && current.direction === "asc" ? "desc" : "asc",
  }));
};

const sortedSessions = [...sessions].sort((a, b) => {
  if (sortConfig.key === "name") {
    const comparison = (a.clientName || "").localeCompare(
      b.clientName || "",
      undefined,
      { sensitivity: "base" }
    );
    return sortConfig.direction === "asc" ? comparison : -comparison;
  }

  const aSession = getClientNextSession(a);
  const bSession = getClientNextSession(b);
  const aTime = aSession.start?.getTime();
  const bTime = bSession.start?.getTime();

  // Keep clients without a scheduled session at the bottom in either direction.
  if (aTime === undefined && bTime === undefined) {
    return (a.clientName || "").localeCompare(b.clientName || "");
  }
  if (aTime === undefined) return 1;
  if (bTime === undefined) return -1;

  return sortConfig.direction === "asc" ? aTime - bTime : bTime - aTime;
});

const sortIndicator = (key) => {
  if (sortConfig.key !== key) return "↕";
  return sortConfig.direction === "asc" ? "↑" : "↓";
};

const handleJoin = (session) => {
  if (!session) return;

  const userId = localStorage.getItem("PlutonicuserID");

  sessionStorage.setItem("meetingID", session.meetingID);
  sessionStorage.setItem("userId", userId);

  navigate(`/unity-room/${session.roomType}`);
};

async function handleUnlock(session) {
  const { sessionID } = session;

  // Optimistic UI: mark as unlocking + clear the lock locally
  setUnlocking(prev => new Set(prev).add(sessionID));
  setSessions(prev =>
    prev.map(s =>
      s.sessionID === sessionID ? { ...s, locked: false } : s
    )
  );

  try {
    // 🔧 Call your Flask route
    const res = await axiosInstance.post(`/api/set-locked-status/${sessionID}`, {
      locked: false, // explicitly unlock
    });

    if (res.status < 200 || res.status >= 300) {
      throw new Error(`HTTP ${res.status}`);
    }

    console.log(`✅ Successfully unlocked ${sessionID}`, res.data);
  } catch (err) {
    // Revert on error
    console.error('❌ Unlock error:', err);
    setSessions(prev =>
      prev.map(s =>
        s.sessionID === sessionID ? { ...s, locked: true } : s
      )
    );
    alert('Failed to unlock. Please try again.');
  } finally {
    setUnlocking(prev => {
      const next = new Set(prev);
      next.delete(sessionID);
      return next;
    });
  }
}



  const handleViewHistory = (session) => {
    console.log("🪵 DEBUG: handleViewHistory called with", session);
    const sessionID = session.sessionID;
    if (sessionID) {
      navigate(`/clients/${sessionID}/overview`);
    } else if (session.isPlaceholder) {
      alert(`${session.clientName} has no AI companion sessions yet.`);
    } else {
      if (onViewHistory) {
        const clientWithSession = {
          id: session.clientId,
          name: session.clientName,
          nickname: session.clientNickname,
          preferredSpace: session.preferredSpace,
          userType: session.userType || "Client",
          sessions: [session],
        };
        console.log("🪵 DEBUG: Triggering onViewHistory with", clientWithSession);
        onViewHistory(clientWithSession);
      }
    }
  };

  if (loading || externalLoading) {
    return (
      <div className="bg-white rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-blue-600">Clients</h3>
        </div>
        <div className="px-6 py-12 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-500 mt-2">Loading therapy sessions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-blue-600">Clients</h3>
        </div>
        <div className="px-6 py-12 text-center">
          <div className="text-red-500 mb-2">
            <svg
              className="w-8 h-8 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              ></path>
            </svg>
          </div>
          <p className="text-red-600 font-medium">Error loading sessions</p>
          <p className="text-gray-500 text-sm mt-1">{error}</p>
          <button
            onClick={fetchSessions}
            className="mt-3 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Compute which clients have locked sessions
  const lockedClients = new Set(
    sessions
      .filter((s) => s.locked === true)
      .map((s) => s.clientName)
  );
  return (
    <div className="bg-white rounded-lg overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
        <h3 className="text-lg font-semibold text-blue-600">Clients</h3>
        <button
          onClick={() => {
            if (fetchClientsData) {
              fetchClientsData(true).then((data) => {
                if (data) {
                  processClientsData(data);
                }
              });
            } else {
              fetchSessions();
            }
          }}
          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
        >
          <img src={refreshIcon} alt="Refresh" className="w-8 h-8" />
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-200">
            <tr>
              <th
                className="px-6 py-3 text-left text-sm font-bold text-black-700 tracking-wider"
                aria-sort={
                  sortConfig.key === "name"
                    ? sortConfig.direction === "asc"
                      ? "ascending"
                      : "descending"
                    : "none"
                }
              >
                <button
                  type="button"
                  onClick={() => handleSort("name")}
                  className="inline-flex items-center gap-1 hover:text-blue-700"
                >
                  Name <span aria-hidden="true">{sortIndicator("name")}</span>
                </button>
              </th>
              <th className="px-6 py-3 text-left text-sm font-bold text-black-700 tracking-wider">
                Nickname
              </th>
              <th className="px-6 py-3 text-left text-sm font-bold text-black-700 tracking-wider">
                Preferred Space
              </th>
              <th
                className="px-6 py-3 text-left text-sm font-bold text-black-700 tracking-wider"
                aria-sort={
                  sortConfig.key === "nextSession"
                    ? sortConfig.direction === "asc"
                      ? "ascending"
                      : "descending"
                    : "none"
                }
              >
                <button
                  type="button"
                  onClick={() => handleSort("nextSession")}
                  className="inline-flex items-center gap-1 hover:text-blue-700"
                >
                  Next Session
                  <span aria-hidden="true">{sortIndicator("nextSession")}</span>
                </button>
              </th>
              <th className="px-6 py-3 text-left text-sm font-bold">
                Online
              </th>
              <th className="px-6 py-3 text-left text-sm font-bold text-black-700 tracking-wider">
                Worksheets
              </th>
              <th className="px-6 py-3 text-left text-sm font-bold text-black-700 tracking-wider">
                AI Companion History
              </th>

              <th className="px-6 py-3 text-left text-sm font-bold text-black-700 tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedSessions.map((session, index) => (
              <tr key={session.sessionID || index} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <div className="flex items-center gap-2">
                    {lockedClients.has(session.clientName) && (
                      <img
                        src={flagIcon}
                        alt="Locked session"
                        title="Client has locked sessions"
                        className="w-4 h-4"
                      />
                    )}
                    <span className={lockedClients.has(session.clientName) ? "pl-1" : ""}>
                      {session.clientName || "Unknown Client"}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {session.clientNickname || "Unknown"}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {session.preferredSpace || "Unknown Space"}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {(() => {
                    const nextSession = getClientNextSession(session);

                    if (nextSession.status === "active") {
                      return (
                        <div>
                          <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-1 font-medium text-green-700">
                            Available now
                          </span>
                          <div className="mt-1 text-xs text-gray-500">
                            {formatSessionTime(nextSession.start)}
                          </div>
                        </div>
                      );
                    }

                    if (nextSession.status === "upcoming") {
                      return (
                        <span className="text-gray-700">
                          {formatSessionTime(nextSession.start)}
                        </span>
                      );
                    }

                    return (
                      <span className="text-gray-400">Not scheduled</span>
                    );
                  })()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {(() => {
                    const onlineSession = getClientOnlineSession(session);

                    if (!onlineSession) {
                      return <span className="text-gray-400">—</span>;
                    }

                    return (
                      <button
                        onClick={() => handleJoin(onlineSession)}
                        className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                      >
                        Join
                      </button>
                    );
                  })()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <button
                    onClick={() => {}}
                    className="text-gray-400 font-medium cursor-not-allowed"
                    disabled
                  >
                    View
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {session.userType === 'client-ai-enabled-therapy' || session.userType === 'client-ai-companion' ? (
                    <button
                      onClick={() => handleViewHistory(session)}
                      className={`font-medium ${
                        session.isPlaceholder 
                          ? "text-gray-500 hover:text-gray-700" 
                          : "text-blue-600 hover:text-blue-800"
                      }`}
                    >
                      {session.isPlaceholder ? "No Sessions" : "View"}
                    </button>
                  ) : (
                    <span className="text-gray-400 font-medium">
                      No History
                    </span>
                  )}
                </td>

                <td className="px-6 py-4 whitespace-nowrap text-sm">
  <button
    onClick={() => {
      console.log("🟣 Schedule Session clicked for client:", session.clientId);
      navigate("/clients/schedule", {
        state: {
          clientId: session.clientId,
          clientEmail: session.clientEmail,
          clientName: session.clientName,
        }
      });
    }}
    className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium transition"
  >
    Schedule Session
  </button>
</td>


              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {sessions.length === 0 && !loading && !error && (
        <div className="px-6 py-12 text-center">
          <p className="text-gray-500">No therapy sessions found.</p>
          <p className="text-gray-400 text-sm mt-1">
            Therapy session summaries will appear here once clients have
            sessions.
          </p>
        </div>
      )}
    </div>
  );
};

export default TherapistClients;
